import { apiClient } from './apiClient';
import { BarInterval, ComparisonOperator, RiskLevel, PositionSide } from '@/services/enums';

export interface CoinMetrics {
  [key: string]: any;
}

/**
 * 结构化条件接口
 */
export interface OrderCondition {
  field: string; // 字段名：price, rsi14, macd, macdHist, macdSignal, cmf14, volume, high, low, sma20, ema20, sma50, ema50 等
  operator: ComparisonOperator; // 关系符号
  value: number | string; // 条件值
  description?: string; // 条件描述（中文，用于显示）
}

/**
 * 获取处理中的订单任务列表
 */
export interface ProcessingOrderTask {
  id: string;
  taskId: string;
  symbol: string;
  direction: PositionSide;
  entryPrice?: number;
  targetPrice?: number;
  stopLossPrice?: number;
  indicators: Array<{
    name: string;
    value: number | string;
    description: string;
  }>;
  conditions: Array<{
    field: string;
    operator: ComparisonOperator;
    value: number | string;
    description?: string;
  }>;
  riskLevel: RiskLevel;
  positionSize: number;
  status: string;
  createdAt: string;
  [key: string]: any;
}

export class CoinMetricsService {
  /**
   * 提交指标计算任务（异步处理）
   * @param symbol 币种符号
   * @param timeframe 时间粒度，默认 "1H"
   */
  async submitCalculateMetricsTask(
    symbol: string,
    timeframe: BarInterval = '1H',
  ): Promise<{ taskId: string; status: string }> {
    const response = await apiClient.post<{ taskId: string; status: string }>(
      `/coin-metrics/calculate/${encodeURIComponent(symbol)}`,
      { timeframe },
    );

    if (!response.success) {
      const errorMessage = response.error || response.message || '指标计算任务提交失败';
      throw new Error(errorMessage);
    }

    return response.data;
  }

  /**
   * 查询指标计算任务状态
   */
  async getCalculateMetricsStatus(symbol: string, taskId?: string): Promise<any> {
    const params = taskId ? `?taskId=${encodeURIComponent(taskId)}` : '';
    const response = await apiClient.get<any>(`/coin-metrics/calculate/${encodeURIComponent(symbol)}/status${params}`);

    if (!response.success) {
      const errorMessage = response.error || response.message || '查询任务状态失败';
      throw new Error(errorMessage);
    }

    return response.data;
  }

  /**
   * 计算单个币种的指标（异步处理，兼容旧接口）
   * @param symbol 币种符号
   * @param timeframe 时间粒度，默认 "1H"
   */
  async calculateCoinMetrics(symbol: string, timeframe: BarInterval = '1H'): Promise<CoinMetrics> {
    // 提交任务
    const taskResult = await this.submitCalculateMetricsTask(symbol, timeframe);

    // 如果没有taskId，说明是同步返回的缓存结果
    if (!taskResult.taskId) {
      throw new Error('无法获取任务ID');
    }

    // 轮询任务状态
    const maxAttempts = 120; // 最多轮询120次（4分钟）
    const pollInterval = 2000; // 每2秒轮询一次

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, pollInterval));

      const statusResult = await this.getCalculateMetricsStatus(symbol, taskResult.taskId);

      // 处理响应格式：{ success, status, data, error, ... }
      // 如果 getCalculateMetricsStatus 返回 data (payload)，那么 status 在 payload 中
      // 或者是 payload 本身包含 status?
      // 通常 status 接口返回 { success: true, data: { status: 'completed', data: ... } }
      // 所以 statusResult 就是 { status: 'completed', data: ... }
      const status = statusResult?.status;
      const data = statusResult?.data;
      const error = statusResult?.error;

      console.log(`[指标计算] 轮询状态 (尝试 ${attempt + 1}/${maxAttempts}):`, {
        status,
        hasData: !!data,
        hasError: !!error,
        progress: statusResult?.progress,
        message: statusResult?.message,
      });

      if (status === 'completed') {
        if (data) {
          console.log(`[指标计算] 计算完成，返回结果`);
          return data;
        }
        // 如果状态是completed但没有data，继续等待
        continue;
      }

      if (status === 'failed') {
        throw new Error(error || '指标计算失败');
      }

      // processing 或其他状态，继续轮询
    }

    // 超时
    throw new Error('指标计算超时，请稍后重试');
  }

  /**
   * 获取币种指标数据
   * @param symbol 币种符号
   * @param timeframe 时间粒度，默认 "1H"
   */
  async getCoinMetrics(symbol: string, timeframe: string = '1H'): Promise<CoinMetrics | null> {
    const response = await apiClient.get<CoinMetrics>(
      `/coin-metrics/${encodeURIComponent(symbol)}?timeframe=${encodeURIComponent(timeframe)}`,
    );

    if (!response.success) {
      const errorMessage = response.message || response.error || '获取币种指标失败';
      throw new Error(errorMessage);
    }

    return response.data;
  }

  /**
   * 提交 AI 分析任务（异步处理）
   * @param symbol 币种符号
   * @param timeframe 时间粒度，默认 "1H"
   */
  async submitAIAnalysisTask(symbol: string, timeframe: string = '1H'): Promise<{ taskId: string; status: string }> {
    const response = await apiClient.post<any>(`/coin-metrics/ai-analysis/${encodeURIComponent(symbol)}`, {
      timeframe,
    });

    if (!response.success) {
      const errorMessage = response.error || response.message || 'AI 分析任务提交失败';
      throw new Error(errorMessage);
    }

    return response.data;
  }

  /**
   * 提交AI联网搜索任务（异步处理）
   */
  async submitAIWebSearchTask(symbol: string): Promise<{ taskId: string; status: string; data?: any }> {
    const response = await apiClient.post<{ taskId: string; status: string; data?: any }>(
      `/coin-metrics/ai-web-search/${encodeURIComponent(symbol)}`,
    );

    if (!response.success) {
      const errorMessage = response.error || response.message || 'AI联网搜索任务提交失败';
      throw new Error(errorMessage);
    }

    return response.data;
  }

  /**
   * 查询 AI 联网搜索任务状态
   */
  async getAIWebSearchStatus(symbol: string, taskId?: string): Promise<any> {
    const params = taskId ? `?taskId=${encodeURIComponent(taskId)}` : '';
    const response = await apiClient.get<any>(
      `/coin-metrics/ai-web-search/${encodeURIComponent(symbol)}/status${params}`,
    );

    if (!response.success) {
      const errorMessage = response.error || response.message || '查询任务状态失败';
      throw new Error(errorMessage);
    }

    return response.data;
  }

  /**
   * 调用后端 AI 联网搜索接口（异步处理）
   */
  async searchCoinNewsWithAI(symbol: string): Promise<any> {
    // 提交任务
    const taskResult = await this.submitAIWebSearchTask(symbol);

    // 如果已经有结果（缓存），直接返回
    if (taskResult.status === 'completed' && taskResult.data) {
      return taskResult.data;
    }

    // 如果没有taskId，说明是同步返回的缓存结果
    if (!taskResult.taskId) {
      throw new Error('无法获取任务ID');
    }

    // 轮询任务状态
    const maxAttempts = 60; // 最多轮询60次
    const pollInterval = 2000; // 每2秒轮询一次

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, pollInterval));

      const statusResult = await this.getAIWebSearchStatus(symbol, taskResult.taskId);

      const status = statusResult?.status;
      const data = statusResult?.data;
      const error = statusResult?.error;

      if (status === 'completed') {
        if (data) {
          return data;
        } else {
          if (attempt < 2) {
            continue;
          }
          throw new Error('任务已完成但未返回数据');
        }
      } else if (status === 'failed') {
        throw new Error(error || 'AI 联网搜索失败');
      } else if (status === 'processing') {
        continue;
      } else if (status === 'not_found') {
        if (attempt < 10) {
          continue;
        }
        throw new Error('任务未找到，请重新提交');
      } else {
        if (attempt < 5) {
          continue;
        }
        throw new Error(`未知的任务状态: ${status || '未知'}`);
      }
    }

    throw new Error('AI 联网搜索超时，请稍后重试');
  }

  /**
   * 查询 AI 分析任务状态
   * @param symbol 币种符号
   * @param taskId 任务ID
   * @param timeframe 时间粒度，默认 "1H"
   */
  async getAIAnalysisStatus(symbol: string, taskId?: string, timeframe: string = '1H'): Promise<any> {
    const queryParams = new URLSearchParams();
    if (taskId) queryParams.append('taskId', taskId);
    queryParams.append('timeframe', timeframe);
    const params = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await apiClient.get<any>(
      `/coin-metrics/ai-analysis/${encodeURIComponent(symbol)}/status${params}`,
    );

    if (!response.success) {
      const errorMessage = response.error || response.message || '查询任务状态失败';
      throw new Error(errorMessage);
    }

    return response.data;
  }

  /**
   * AI分析币种指标
   * @param symbol 币种符号
   * @param timeframe 时间粒度，默认 "1H"
   */
  async analyzeCoinMetrics(symbol: string, timeframe: string = '1H'): Promise<any> {
    // 提交任务
    const taskResult = await this.submitAIAnalysisTask(symbol, timeframe);

    // 如果已经有结果（缓存），直接返回
    if ((taskResult as any).status === 'completed' && (taskResult as any).data) {
      return (taskResult as any).data;
    }

    // 如果没有taskId，说明是同步返回的缓存结果（或 submitAIAnalysisTask 已处理）
    if (!(taskResult as any).taskId) {
      // 如果 submitAIAnalysisTask 返回了 data 但没 taskId，可能是直接结果
      // 但我们在 submitAIAnalysisTask 已经做了处理。
      // 这里只是再次检查
      if ((taskResult as any).data) return (taskResult as any).data;
      throw new Error('无法获取任务ID');
    }

    // 轮询任务状态
    const maxAttempts = 60; // 最多轮询60次
    const pollInterval = 2000; // 每2秒轮询一次

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, pollInterval));

      const statusResult = await this.getAIAnalysisStatus(symbol, (taskResult as any).taskId, timeframe);

      const status = statusResult?.status;
      const data = statusResult?.data;
      const error = statusResult?.error;

      console.log(`[AI分析] 轮询状态 (尝试 ${attempt + 1}/${maxAttempts}):`, {
        status,
        hasData: !!data,
        hasError: !!error,
        fullResponse: statusResult,
      });

      if (status === 'completed') {
        if (data) {
          return data;
        } else {
          if (attempt < 2) {
            continue;
          }
          throw new Error('任务已完成但未返回数据');
        }
      } else if (status === 'failed') {
        throw new Error(error || 'AI 分析失败');
      } else if (status === 'processing') {
        continue;
      } else if (status === 'not_found') {
        if (attempt < 10) {
          continue;
        }
        throw new Error('任务未找到，请重新提交');
      } else {
        console.warn('未知的任务状态:', { status, statusResult, attempt: attempt + 1 });
        if (attempt < 5) {
          continue;
        }
        throw new Error(`未知的任务状态: ${status || '未知'}`);
      }
    }

    throw new Error('AI 分析超时，请稍后重试');
  }

  /**
   * 提交AI下单分析任务（异步处理）
   * @param symbol 币种符号
   * @param recommendedStrategy 操作建议
   * @param timeframe 时间粒度，默认 "1H"
   */
  async submitAITradingStrategyTask(
    symbol: string,
    recommendedStrategy: string,
    timeframe: string = '1H',
  ): Promise<{ taskId: string; status: string; data?: any }> {
    const response = await apiClient.post<{ taskId: string; status: string; data?: any }>(
      `/coin-metrics/${encodeURIComponent(symbol)}/ai-order-analysis`,
      {
        recommendedStrategy,
        timeframe,
      },
    );

    if (!response.success) {
      const errorMessage = response.error || response.message || 'AI交易策略分析任务提交失败';
      throw new Error(errorMessage);
    }

    return response.data;
  }

  /**
   * 查询AI下单分析任务状态
   */
  async getAITradingStrategyStatus(symbol: string, taskId?: string): Promise<any> {
    const params = taskId ? `?taskId=${encodeURIComponent(taskId)}` : '';
    const response = await apiClient.get<any>(
      `/coin-metrics/${encodeURIComponent(symbol)}/ai-order-analysis/status${params}`,
    );

    if (!response.success) {
      const errorMessage = response.error || response.message || '查询任务状态失败';
      throw new Error(errorMessage);
    }

    return response.data;
  }

  /**
   * AI分析操作建议并生成交易策略（异步处理，兼容旧接口）
   * @param symbol 币种符号
   * @param recommendedStrategy 操作建议
   * @param timeframe 时间粒度，默认 "1H"
   */
  async analyzeTradingStrategy(symbol: string, recommendedStrategy: string, timeframe: string = '1H'): Promise<any> {
    // 提交任务
    const taskResult = await this.submitAITradingStrategyTask(symbol, recommendedStrategy, timeframe);

    // 如果已经有结果（缓存），直接返回
    if (taskResult.status === 'completed' && taskResult.data) {
      return taskResult.data;
    }

    // 如果没有taskId，说明是同步返回的缓存结果
    if (!taskResult.taskId) {
      throw new Error('无法获取任务ID');
    }

    // 轮询任务状态
    const maxAttempts = 60; // 最多轮询60次（2分钟）
    const pollInterval = 2000; // 每2秒轮询一次

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, pollInterval));

      const statusResult = await this.getAITradingStrategyStatus(symbol, taskResult.taskId);

      const status = statusResult?.status;
      const data = statusResult?.data || statusResult?.result;
      const error = statusResult?.error;

      console.log(`[AI下单分析] 轮询状态 (尝试 ${attempt + 1}/${maxAttempts}):`, {
        status,
        hasData: !!data,
        hasError: !!error,
        fullResponse: statusResult,
      });

      if (status === 'completed') {
        if (data) {
          return data;
        } else {
          if (attempt < 2) {
            continue;
          }
          throw new Error('任务已完成但未返回数据');
        }
      } else if (status === 'failed') {
        throw new Error(error || 'AI下单分析失败');
      } else if (status === 'processing') {
        continue;
      } else if (status === 'not_found') {
        if (attempt < 10) {
          continue;
        }
        throw new Error('任务未找到，请重新提交');
      } else {
        console.warn('未知的任务状态:', { status, statusResult, attempt: attempt + 1 });
        if (attempt < 5) {
          continue;
        }
        throw new Error(`未知的任务状态: ${status || '未知'}`);
      }
    }

    throw new Error('AI下单分析超时，请稍后重试');
  }

  /**
   * 提交下单任务到队列（执行策略，等待条件满足）
   */
  async submitOrderTask(
    symbol: string,
    direction: PositionSide,
    entryPrice?: number,
    targetPrice?: number,
    stopLossPrice?: number,
    indicators?: Array<{ name: string; value: number | string; description: string }>,
    conditions?: OrderCondition[],
    riskLevel?: RiskLevel,
    positionSize?: number,
    timeframe?: string,
  ): Promise<{ taskId: string; message: string }> {
    const response = await apiClient.post<{ taskId: string; message: string }>(
      `/coin-metrics/${encodeURIComponent(symbol)}/submit-order`,
      {
        direction,
        entryPrice,
        targetPrice,
        stopLossPrice,
        indicators: indicators || [],
        conditions: conditions || [],
        riskLevel: riskLevel || 'medium',
        positionSize: positionSize || 2000,
        timeframe: timeframe || '1H',
      },
    );

    if (!response.success) {
      const errorMessage = response.error || response.message || '提交下单任务失败';
      throw new Error(errorMessage);
    }

    return response.data;
  }

  /**
   * 立即执行下单（不等待条件，直接下单）
   */
  async executeOrderImmediately(
    symbol: string,
    direction: PositionSide,
    entryPrice?: number,
    targetPrice?: number,
    stopLossPrice?: number,
    indicators?: Array<{ name: string; value: number | string; description: string }>,
    conditions?: OrderCondition[],
    riskLevel?: RiskLevel,
    positionSize?: number,
    timeframe?: string,
  ): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>(
      `/coin-metrics/${encodeURIComponent(symbol)}/execute-order-immediately`,
      {
        direction,
        entryPrice,
        targetPrice,
        stopLossPrice,
        indicators: indicators || [],
        conditions: conditions || [],
        riskLevel: riskLevel || 'medium',
        positionSize: positionSize || 2000,
        timeframe: timeframe || '1H',
      },
    );

    if (!response.success) {
      const errorCode = (response as any).code || 'UNKNOWN';
      const errorMessage = response.error || response.message || '立即执行下单失败';
      const error = new Error(errorMessage);
      (error as any).code = errorCode;
      (error as any).data = response.data;
      throw error;
    }

    return response.data;
  }

  /**
   * 获取处理中的订单任务列表
   */
  async getProcessingOrders(): Promise<ProcessingOrderTask[]> {
    const response = await apiClient.get<{ tasks: ProcessingOrderTask[] }>('/coin-metrics/processing-orders');

    if (!response.success) {
      throw new Error(response.message || '获取处理中的订单任务失败');
    }

    return response.data?.tasks || [];
  }

  /**
   * 删除处理中的订单任务
   */
  async deleteProcessingOrder(taskId: string): Promise<any> {
    const response = await apiClient.delete<any>(`/coin-metrics/processing-orders/${taskId}`);

    if (!response.success) {
      throw new Error(response.message || '删除订单任务失败');
    }

    return response.data;
  }
}

export const coinMetricsService = new CoinMetricsService();
